import React, { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, Home, Building2, Calendar, DollarSign, 
  AlertCircle, CheckCircle, Clock, Users, MessageSquare,
  TrendingUp, Award, Brain, Pizza, Zap, Heart,
  Briefcase, Video, Phone, Mail, Gauge, LogOut,
  Sun, Moon, Cloud, Umbrella, ThermometerSun,
  Activity, Battery, Wifi, WifiOff, Volume2
} from 'lucide-react';
import { ButtonGrid } from './ButtonGrid';
import { StatusBar } from './StatusBar';
import { Analytics } from './Analytics';
import { Settings } from './Settings';
import { HabitButtons } from './HabitButtons';
import { useWorkStore } from '../../store/work-buttons/workStore';

export default function App() {
  const [activeView, setActiveView] = useState<'buttons' | 'habits' | 'analytics' | 'settings'>('buttons');
  const { user, setUser, theme, setTheme } = useWorkStore();

  useEffect(() => {
    // Check for saved user
    const savedUser = localStorage.getItem('workbuttons_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  if (!user) {
    return <OnboardingScreen onComplete={setUser} />;
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Header 
          user={user}
          activeView={activeView}
          onViewChange={setActiveView}
          theme={theme}
          onThemeToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        />

        <AnimatePresence mode="wait">
          {activeView === 'buttons' && (
            <motion.div
              key="buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <StatusBar />
              <ButtonGrid userRole={user.role} />
            </motion.div>
          )}
          
          {activeView === 'habits' && (
            <motion.div
              key="habits"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <HabitButtons />
            </motion.div>
          )}
          
          {activeView === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Analytics />
            </motion.div>
          )}
          
          {activeView === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Settings />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

interface HeaderProps {
  user: any;
  activeView: string;
  onViewChange: (view: 'buttons' | 'habits' | 'analytics' | 'settings') => void;
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
}

function Header({ user, activeView, onViewChange, theme, onThemeToggle }: HeaderProps) {
  return (
    <header className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="text-4xl"
          >
            🔘
          </motion.div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              WorkButtons
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Hey {user.name}! One click, done. ✨
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={onThemeToggle}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-md"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-md p-1">
            <TabButton 
              active={activeView === 'buttons'} 
              onClick={() => onViewChange('buttons')}
              icon={<Zap size={18} />}
              label="Work"
            />
            <TabButton 
              active={activeView === 'habits'} 
              onClick={() => onViewChange('habits')}
              icon={<Heart size={18} />}
              label="Habits"
            />
            <TabButton 
              active={activeView === 'analytics'} 
              onClick={() => onViewChange('analytics')}
              icon={<TrendingUp size={18} />}
              label="Analytics"
            />
            <TabButton 
              active={activeView === 'settings'} 
              onClick={() => onViewChange('settings')}
              icon={<Briefcase size={18} />}
              label="Settings"
            />
          </div>
        </div>
      </div>
    </header>
  );
}

function TabButton({ active, onClick, icon, label }: any) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-md transition-all
        ${active 
          ? 'bg-blue-500 text-white' 
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}
      `}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function OnboardingScreen({ onComplete }: { onComplete: (user: any) => void }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    role: 'employee' as 'employee' | 'manager',
    department: '',
    email: ''
  });

  const handleSubmit = () => {
    const user = {
      ...formData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    localStorage.setItem('workbuttons_user', JSON.stringify(user));
    onComplete(user);
    toast.success(`Welcome to WorkButtons, ${formData.name}!`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <motion.div
            animate={{ rotate: [0, 10, -10, 10, 0] }}
            transition={{ duration: 0.5, repeat: 3 }}
            className="text-6xl mb-4"
          >
            🔘
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">WorkButtons</h1>
          <p className="text-gray-600">Let's get you set up in seconds!</p>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <input
                type="text"
                placeholder="What's your name?"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <input
                type="email"
                placeholder="Your work email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => setStep(2)}
                disabled={!formData.name || !formData.email}
                className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                Continue
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">I am a...</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, role: 'employee' })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.role === 'employee' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className="mx-auto mb-2" size={24} />
                    <span className="text-sm font-medium">Team Member</span>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, role: 'manager' })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.role === 'manager' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Briefcase className="mx-auto mb-2" size={24} />
                    <span className="text-sm font-medium">Manager</span>
                  </button>
                </div>
              </div>

              <input
                type="text"
                placeholder="Department (e.g., Engineering, Sales)"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.department}
                  className="flex-1 bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  Let's Go! 🚀
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}